import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import {
  QueryClient,
  type QueryClientConfig,
  QueryClientProvider as RQQueryClientProvider,
  type QueryClientProviderProps as RQQueryClientProviderProps,
} from '@tanstack/react-query'
// React Query Devtools are only included in bundles when process.env.NODE_ENV === 'development'
// eslint-disable-next-line import-x/no-extraneous-dependencies
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import {
  PersistQueryClientProvider as RQPersistQueryClientProvider,
  removeOldestQuery,
} from '@tanstack/react-query-persist-client'

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: {
      persist?: boolean
    }
  }
}

type QueryClientProviderProps = Omit<RQQueryClientProviderProps, 'client'>

const CACHING_MAX_TIME = 1000 * 60 * 60 * 24 // 24h

const queryClientParams: QueryClientConfig = {
  defaultOptions: {
    queries: {
      gcTime: CACHING_MAX_TIME,
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
}

export const queryClient = new QueryClient(queryClientParams)
const localStoragePersister = createSyncStoragePersister({
  storage: window.localStorage,
  retry: removeOldestQuery,
})

export function PersistQueryClientProvider({ children }: QueryClientProviderProps) {
  return (
    <RQPersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: localStoragePersister,
        maxAge: CACHING_MAX_TIME,
        dehydrateOptions: {
          // only persist queries that was marked with the `persist: true` meta attribute
          shouldDehydrateQuery: (query) =>
            query.state.status === 'success' && !!query.meta?.persist,
        },
      }}
    >
      {children}
      <ReactQueryDevtools />
    </RQPersistQueryClientProvider>
  )
}

export function QueryClientProvider({ children }: QueryClientProviderProps) {
  return (
    <RQQueryClientProvider client={queryClient}>
      {children}
      <ReactQueryDevtools />
    </RQQueryClientProvider>
  )
}
